import React from 'react';
import { rgba } from 'polished';
import PropTypes from 'prop-types';
import { imageFallback } from '../../../helpers';
import styled, { useTheme, css } from 'styled-components/macro';
import cardImageDark from '../../../img/cardPlaceHolderDark.png';
import cardImageLight from '../../../img/cardPlaceHolderLight.png';
import cardBorkedImageDark from '../../../img/cardBrokenImageDark.png';
import cardBorkedImageLight from '../../../img/cardBrokenImageLight.png';
import Isvg from 'react-inlinesvg';
import close from '../../../img/icons/close.svg';
import { Link } from 'react-router-dom';
import liveDot from '../../../img/icons/material/radio_button_checked_white.svg';

export const Card = ({
  strings,
  orientation,
  state,
  url,
  image,
  tag,
  title,
  subtitle,
  progress,
  altText,
  isSlick,
  children,
  cardBtn,
  details,
  isLive,
  onLater,
  isToday,
  active,
  type,
}) => {
  const theme = useTheme();
  const unavailable = state === 'unavailable' ? true : false;
  const live = isLive ? true : false;
  const onlater = onLater ? true : false;
  const subtitleClean = subtitle === title ? null : subtitle;

  const CardItemWrapper = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children;

  return (
    <CardContainer isSlick={isSlick} type={type}>
      {cardBtn && (
        <CardBtn
          onClick={(event) => cardBtn.btnFunction({ event, ...details })}
        >
          <SVG src={close} />
          <ScreenReaderText>{cardBtn.btnText}</ScreenReaderText>
          <BtnText>{cardBtn.btnText}</BtnText>
        </CardBtn>
      )}
      <CardItemWrapper
        condition={
          isLive ? true : state === 'available' && !onlater ? true : false
        }
        wrapper={(children) => {
          if (/^(?:[a-z]+:)?\/\//i.test(url)) {
            return <a href={url}>{children}</a>;
          } else {
            return <Link to={url}>{children}</Link>;
          }
        }}
      >
        <CardInner className="card__inner" orientation={orientation}>
          <CardImage
            style={
              !unavailable
                ? {
                    backgroundImage:
                      theme.name === 'light'
                        ? `url('${cardImageLight}')`
                        : `url('${cardImageDark}')`,
                  }
                : null
            }
            active={active}
            orientation={orientation}
          >
            <Image
              src={
                image
                  ? image
                  : theme.name === 'light'
                  ? cardImageLight
                  : cardImageDark
              }
              alt={altText}
              onError={(e) =>
                imageFallback(
                  e,
                  theme.name === 'light'
                    ? cardBorkedImageLight
                    : cardBorkedImageDark
                )
              }
              unavailable={unavailable}
              live={live}
              onlater={onlater}
              orientation={orientation}
              loading="lazy"
            />

            {unavailable && !isToday && (
              <CardImageUnavailable>
                {strings ? strings.unavailable : 'Unavailable'}
              </CardImageUnavailable>
            )}
            {live && (
              <CardImageLive>{strings ? strings.live : 'Live'}</CardImageLive>
            )}
            {!live && (onlater || isToday) && (
              <CardImageOnLater>
                {strings ? strings.onLater : 'On Later'}
              </CardImageOnLater>
            )}
          </CardImage>
          <CardContent orientation={orientation}>
            {progress > 0 && <ProgressBar percentage={progress % 100} />}
            {tag && <CardTag>{tag}</CardTag>}
            {title && <CardTitle>{title}</CardTitle>}
            {subtitleClean && <CardSubtitle>{subtitle}</CardSubtitle>}
            {children}
          </CardContent>
        </CardInner>
      </CardItemWrapper>
    </CardContainer>
  );
};

Card.propTypes = {
  state: PropTypes.oneOf(['available', 'unavailable', 'live', 'onlater']),
  url: PropTypes.string,
  image: PropTypes.string,
  tag: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  progress: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  altText: PropTypes.string,
  isSlick: PropTypes.bool,
};

Card.defaultProps = {
  state: 'available',
  progress: 0,
  isSlick: false,
};

const CardBtn = styled.button`
  align-items: center;
  background: ${(props) => props.theme.color.whiteFull};
  border-radius: 20px;
  border: none;
  box-shadow: 0 0 0 2px ${(props) => rgba(props.theme.color.blackFull, 0.15)};
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  height: 24px;
  justify-content: center;
  line-height: normal;
  margin: 0;
  overflow: visible;
  padding: 0;
  position: absolute;
  right: 10px;
  top: 6px;
  width: auto;
  width: 24px;
  -webkit-font-smoothing: inherit;
  -moz-osx-font-smoothing: inherit;
  -webkit-appearance: none;
  z-index: 10;

  &:hover {
    background: ${(props) => props.theme.color.statusRed};
    box-shadow: 0 0 0 2px ${(props) => rgba(props.theme.color.statusRed, 0.5)};
  }
`;

const SVG = styled(Isvg)`
  display: flex;
  height: 12px;
  line-height: 1;
  width: 12px;
  svg * {
    fill: ${(props) => props.theme.color.statusRed};
  }
  ${CardBtn}:hover & svg * {
    fill: ${(props) => props.theme.color.whiteFull};
  }
`;
const ScreenReaderText = styled.span`
  border: 0;
  clip: rect(1px, 1px, 1px, 1px);
  -webkit-clip-path: inset(50%);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute !important;
  width: 1px;
  word-wrap: normal !important;
`;
const BtnText = styled.span`
  display: none;
`;

const CardImage = styled.div`
  height: 0;
  padding-bottom: 56.25%;

  width: 100%;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 8px;
  position: relative;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? `0px 16px 32px rgba(0, 0, 0, 0.05),
  0px 8px 16px rgba(0, 0, 0, 0.05), 0px 4px 8px rgba(0, 0, 0, 0.05),
  0px 2px 4px rgba(0, 0, 0, 0.05)`
      : `0 0 0 1px #3F3F3E, 0px 2px 2px -3px rgba(254, 246, 232, 0.5), 0px 18px 32px -4px rgba(164, 71, 10, 0.05), 0px 12px 16px -6px rgba(248, 165, 27, 0.05), 0px 6px 8px -4px rgba(252, 210, 141, 0.05)`};
  ${(props) =>
    props.orientation === 'horizontal' &&
    `width: 72px;
       height: 72px;
       margin-right: 18px;
       padding-bottom: 0;
    `};

  ${(props) =>
    props.active &&
    css`
      &:before {
        content: '';
        display: block;
        bottom: -8px;
        left: -8px;
        right: -8px;
        top: -8px;
        position: absolute;
        border: 4px solid ${(props) => props.theme.color.primaryFull};
        border-radius: 16px;
      }
    `}
  img {
    border-radius: 8px;
  }
`;

const CardContainer = styled.div`
  ${(props) => props.theme.fontBook};
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  ${(props) => (props.isSlick ? 'margin: 0 6px' : 'margin: 0')};
  padding: 0;
  position: relative;
  ${(props) =>
    props.type === 'az' &&
    `max-width: 100%;
     width: 100%;
     margin: 0 6px 24px 0 !important;
     &:nth-last-child(1),
     &:nth-last-child(2),
     &:nth-last-child(3),
     &:nth-last-child(4),
     &:nth-last-child(5) {
      margin-bottom: 24px !important;
    } 
  `};
  @media screen and (min-width: 768px) {
    ${(props) =>
      props.type === 'az' && `margin-bottom: 50px !important;`};
  }
  a {
    width: 100%;
    display: flex;
    text-decoration: none;
    &:hover {
      box-shadow: none !important;
      cursor: pointer;
      h3,
      h4,
      h5 {
        color: ${(props) =>
          props.theme.name === 'light'
            ? props.theme.color.primaryAA
            : props.theme.color.primaryFull};
      }
      ${CardImage} {
        box-shadow: ${(props) =>
          props.theme.name === 'light'
            ? `0px 32px 64px rgba(0, 0, 0, 0.075), 0px 16px 32px rgba(0, 0, 0, 0.075), 0px 8px 16px rgba(0, 0, 0, 0.075), 0px 4px 8px rgba(0, 0, 0, 0.075)`
            : `0px 2px 2px -3px #FEF6E8, 0px 18px 32px -4px rgba(164, 71, 10, 0.05), 0px 12px 16px -6px ${props.theme.color.primary5}, 0px 6px 8px -4px rgba(252, 210, 141, 0.05), 0px 3px 4px -2px rgba(253, 232, 198, 0.05), 0px 14px 23px -12px rgba(164, 71, 10, 0.14)`};
      }
    }
    &:focus,
    &:focus-within,
    &:focus-visible {
      box-shadow: inset 0px 0px 0px 0px
        ${(props) =>
          props.theme.name === 'light'
            ? `${rgba(props.theme.color.whiteFull, 0)}`
            : props.theme.color.blackFull};
      outline: none;
      &:focus,
      &:focus-within,
      &:focus-visible {
        &:before {
          content: '';
          display: block;
          bottom: -8px;
          left: -8px;
          right: -8px;
          top: -8px;
          position: absolute;
          border: 4px solid
            ${(props) =>
              props.theme.name === 'light'
                ? props.theme.color.statusLightFocus
                : props.theme.color.statusDarkFocus};
          border-radius: 16px;
        }
      }
    }
  }
`;

const CardInner = styled.div`
  display: flex;
  width: 100%;
  ${(props) =>
    props.orientation === 'horizontal'
      ? 'flex-direction: row'
      : 'flex-direction: column'}
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  ${(props) =>
    props.orientation === 'horizontal'
      ? 'width: 72px; height: 72px; object-fit: cover;'
      : 'max-width: 100%; width: 100%;'}
  ${(props) =>
    props.unavailable &&
    `
    filter: grayscale(0.75);
    opacity: 0.75;
  `}
`;

const CardImageState = styled.div`
  ${(props) => props.theme.font.overline};
  position: absolute;
  top: 0;
  left: 0;
  margin: 8px;
  padding: 1px 8px 1px 6px;
  backdrop-filter: blur(24px);
  border-radius: 2px;
  font-size: 12px;
  line-height: 1.5em;
  font-weight: 500;
  letter-spacing: 1.1px;
`;

const CardImageUnavailable = styled(CardImageState)`
  background: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.unavailableLight
      : props.theme.color.unavailableDark};
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.white90
      : props.theme.color.black90};
`;

const CardImageLive = styled(CardImageState)`
  display: flex;
  align-items: center;
  padding-left: 30px;
  background: ${(props) => props.theme.color.statusRed};
  color: ${(props) => props.theme.color.whiteFull};
  &:before {
    position: absolute;
    left: 6px;
    height: 20px;
    width: 20px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: 20px 20px;
    background-image: url(${liveDot});
    content: '';
  }
`;

const CardImageOnLater = styled(CardImageState)`
  background: ${(props) => props.theme.color.statusAmber};
  color: ${(props) => props.theme.color.blackFull};
`;

const CardContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  margin: 17px 0;
  ${(props) => props.orientation === 'horizontal' && 'flex: 1; margin: 0;'}
`;

const ProgressBar = styled.div`
  height: 2px;
  background-color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black5
      : props.theme.color.white5};
  border-radius: 2px;
  position: relative;
  margin-top: -2px;
  transform: translateY(-5px);
  overflow: hidden;

  &:before {
    display: block;
    ${(props) =>
      props.percentage &&
      `
        width: ${props.percentage}%
      `};
    height: 2px;
    background-color: ${(props) => props.theme.color.primaryFull};
    border-radius: 2px;
    content: '';
  }
`;

const CardTag = styled.h3`
  font-size: 12px;
  line-height: 1.2em;
  margin: 0 0 4px;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.primaryAA
      : props.theme.color.primaryFull};
`;

const CardTitle = styled.h4`
  font-size: 18px;
  margin: 0 0 4px;
  letter-spacing: -0.02em;
  line-height: 1.2em;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black90
      : props.theme.color.white90};
`;

const CardSubtitle = styled.h5`
  font-size: 16px;
  margin: 0;
  line-height: 1.2em;
  font-weight: normal;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black75
      : props.theme.color.white75};
`;
